import React, { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '../../services/authService'
import { exchangePoints, getPointsPromotionalMessage, notifyUserSummaryUpdated } from '../../services/pointsService'
import type { PointsOutletContext } from './ModernPointsLayout'

/** แท็บ แลกเปลี่ยนคะแนนเป็นเงิน */
const PointsPointtomoneyPage: React.FC = () => {
  const {
    pointsText,
    pointsConfig,
    activePointsToCashRules,
    refreshUserSummary,
  } = useOutletContext<PointsOutletContext>()
  const [submittingRuleId, setSubmittingRuleId] = useState<string | null>(null)

  const exchangeEnabled = pointsConfig?.system?.pointsToCashEnabled === true
  const systemStatusText = exchangeEnabled && activePointsToCashRules.length > 0
    ? 'สามารถแลกคะแนนเป็นเงินได้'
    : exchangeEnabled
      ? 'ยังไม่มีรายการแลกคะแนนเป็นเงินที่เปิดใช้งาน'
      : 'ปิดระบบแลกคะแนนเป็นเงิน'
  const statusTone = exchangeEnabled && activePointsToCashRules.length > 0 ? 'ok' : exchangeEnabled ? 'warn' : 'off'
  const promotionalMessage = getPointsPromotionalMessage(pointsConfig)

  const packageList = useMemo(
    () => activePointsToCashRules,
    [activePointsToCashRules]
  )

  const handleExchange = async (ruleId: string) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    const selectedRule = packageList.find((rule) => rule.id === ruleId)

    if (!tenantId || !userId || !selectedRule) {
      await Swal.fire({ icon: 'error', title: 'ไม่พบข้อมูลผู้ใช้', confirmButtonColor: 'var(--htd-primary)' })
      return
    }

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแลกคะแนนเป็นเงิน',
      html: `ใช้ <strong>${Number(selectedRule.inputAmount).toLocaleString('th-TH')}</strong> คะแนน เพื่อรับเครดิต <strong>${Number(selectedRule.outputAmount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> บาท`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: 'var(--htd-primary)',
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRuleId(ruleId)
      const response = await exchangePoints({
        tenantId,
        userId,
        direction: 'points-to-cash',
        ruleId,
      })
      notifyUserSummaryUpdated()
      await refreshUserSummary()
      await Swal.fire({
        icon: 'success',
        title: response.message || 'แลกคะแนนเป็นเงินสำเร็จ',
        confirmButtonColor: 'var(--htd-primary)',
      })
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แลกคะแนนเป็นเงินไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: 'var(--htd-primary)',
      })
    } finally {
      setSubmittingRuleId(null)
    }
  }

  return (
    <div className="htd-pts__stack">
      <h2 className="htd-pts__page-title">แลกเปลี่ยนคะแนนเป็นเงิน</h2>

      <div className="htd-pts__panel">
        <div className="htd-pts__status">
          <span className={`htd-pts__status-icon is-${statusTone}`} aria-hidden>
            <i className={`fas ${statusTone === 'ok' ? 'fa-check' : statusTone === 'warn' ? 'fa-exclamation' : 'fa-ban'}`} />
          </span>
          <div className="htd-pts__status-copy">
            <span>ระบบการแลกเปลี่ยน</span>
            <strong className={`is-${statusTone}`}>{systemStatusText}</strong>
          </div>
        </div>

        <div className="htd-pts__wallet">
          <h3>คะแนนของคุณ</h3>
          <span className="htd-pts__wallet-amount">{pointsText}</span>
        </div>

        {exchangeEnabled && packageList.length > 0 ? (
          <div className="htd-pts__packages">
            {packageList.map((rule) => (
              <article key={rule.id} className="htd-pts__pkg">
                <div className="htd-pts__pkg-media" aria-hidden>
                  <i className="fas fa-wallet" />
                </div>
                <div className="htd-pts__pkg-body">
                  <strong>{Number(rule.inputAmount).toLocaleString('th-TH')} คะแนน</strong>
                  <span>
                    รับ {Number(rule.outputAmount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                  </span>
                </div>
                <button
                  type="button"
                  className="htd-pts__pkg-btn"
                  onClick={() => void handleExchange(rule.id)}
                  disabled={submittingRuleId === rule.id}
                >
                  {submittingRuleId === rule.id
                    ? 'กำลังแลก...'
                    : `${Number(rule.inputAmount).toLocaleString('th-TH')} Pts`}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="htd-pts__empty">ยังไม่มีแพ็กเกจแลกคะแนนเป็นเงินที่เปิดใช้งาน</div>
        )}

        {promotionalMessage ? <p className="htd-pts__promo">{promotionalMessage}</p> : null}
      </div>
    </div>
  )
}

export default PointsPointtomoneyPage
